import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./tokenlyglobal.css";

const ForgotPassword = () => {
  const [mode, setMode] = useState("forgot_phone"); // 'forgot_phone' | 'forgot_reset'
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otpTimeout, setOtpTimeout] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);


  const navigate = useNavigate();

  // OTP Timer
  useEffect(() => {
    let interval;
    if (otpTimeout > 0) {
      interval = setInterval(() => {
        setOtpTimeout((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpTimeout]);

  // ✅ 1. SEND OTP - POST /api/user/forgotpassword
  const handleForgotPhone = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");

    if (!phone || phone.length < 10) {
      setErrorMsg("Please enter a valid 10-digit phone number.");
      return;
    }

    try {
      setLoading(true);
      const res = await fetch("https://tokenly.biswaas.com/api/user/forgotpassword", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone }),
      });
      const json = await res.json();

      if (!json.status) {
        setErrorMsg(json.message || "Unable to send OTP.");
      } else {
        setSuccessMsg("OTP sent! Check your mobile.");
        setOtpTimeout(120);
        setMode("forgot_reset"); // ✅ DIRECT to reset (no separate verify)
      }
    } catch (err) {
      setErrorMsg("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };


  const handleForgotReset = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");

    if (!newPassword || !confirmPassword || !otp) {
      setErrorMsg("Please fill all fields.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMsg("Passwords do not match.");
      return;
    }

    if (newPassword.length < 4) {
      setErrorMsg("Password must be at least 4 characters.");
      return;
    }

    if (otp.length !== 4) {
      setErrorMsg("Please enter valid 4-digit OTP.");
      return;
    }

    try {
      setLoading(true);
      // ✅ POST with body payload
      const res = await fetch("https://tokenly.biswaas.com/api/user/reset-password-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone,
          otp,
          newPassword,
          newPassword_confirmation: confirmPassword
        }),
      });

      const json = await res.json();

      if (!json.status) {
        setErrorMsg(json.message || "Password reset failed.");
      } else {
        setSuccessMsg("Password reset successfully! You can now login.");
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      }
    } catch (err) {
      setErrorMsg("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ 3. RESEND OTP - POST /api/resend-otp
  const handleResendOtp = async () => {
    setErrorMsg("");
    setSuccessMsg("");
    setOtp("");
    setNewPassword("");
    setConfirmPassword("");

    try {
      setLoading(true);
      const res = await fetch("https://tokenly.biswaas.com/api/resend-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone }),
      });
      const json = await res.json();

      if (!json.status) {
        setErrorMsg(json.message || "Unable to resend OTP.");
      } else {
        setSuccessMsg("OTP resent successfully!");
        setOtpTimeout(120); // Reset 2 min timer
      }
    } catch (err) {
      setErrorMsg("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="d-flex align-items-center justify-content-center py-5 pt-5">
      <div className="auth-card shadow-lg rounded-3 p-4 p-md-5 w-100" style={{ maxWidth: 420, marginTop: '80px' }}>
        <button
          type="button"
          className="btn btn-link p-0 mb-3"
          onClick={() => navigate("/login")}
        >
          ← Back to Login
        </button>

        <h3 className="fw-bold mb-1 text-center">
          {mode === "forgot_phone" ? "Reset Password" : "Set New Password"}
        </h3>
        <p className="text-muted mb-4 text-center small">
          {mode === "forgot_phone" && "Enter phone to receive OTP."}
          {mode === "forgot_reset" && "Enter OTP and new password."}
        </p>

        {errorMsg && <div className="alert alert-danger py-2 small">{errorMsg}</div>}
        {successMsg && <div className="alert alert-success py-2 small">{successMsg}</div>}

        {/* PHONE FORM */}
        {mode === "forgot_phone" && (
          <form onSubmit={handleForgotPhone}>
            <div className="mb-3">
              <label className="form-label">Phone Number</label>
              <input
                type="tel"
                className="form-control"
                placeholder="Enter registered phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                maxLength={10}
              />
            </div>
            <button
              type="submit"
              className="btn btn-tokenly-primary w-100 mb-3"
              disabled={loading || phone.length < 10}
            >
              {loading ? "Sending..." : "Send OTP"}
            </button>
          </form>
        )}

        {/* RESET FORM - OTP + Password (2 fields only!) */}
        {mode === "forgot_reset" && (
          <form onSubmit={handleForgotReset}>
            <div className="text-center mb-4 p-3 bg-light rounded">
              <small className="text-muted">
                <strong>OTP sent to:</strong><br />
                <span className="text-dark fw-bold">{phone}</span>
              </small>
              {otpTimeout > 0 && (
                <div className="mt-2">
                  <small className="badge bg-warning text-dark">
                    Resend in {Math.floor(otpTimeout / 60)}:{(otpTimeout % 60).toString().padStart(2, '0')}
                  </small>
                </div>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label">Enter OTP</label>
              <input
                type="text"
                className="form-control form-control-lg text-center"
                placeholder="1234"
                maxLength={4}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ''))}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">New Password</label>
              <div className="position-relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  className="form-control"
                  placeholder="New password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="btn position-absolute top-50 end-0 translate-middle-y border-0 bg-transparent text-muted"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  <i className={`bi ${showNewPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
                </button>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Confirm Password</label>
              <div className="position-relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  className="form-control"
                  placeholder="Repeat password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="btn position-absolute top-50 end-0 translate-middle-y border-0 bg-transparent text-muted"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  <i className={`bi ${showConfirmPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-tokenly-primary w-100 mb-2"
              disabled={loading || newPassword !== confirmPassword || newPassword.length < 4 || otp.length !== 4}
            >
              {loading ? "Resetting..." : "Reset Password"}
            </button>

            <div className="d-flex gap-2 justify-content-center">
              <button
                type="button"
                className="btn btn-link p-0 small"
                onClick={otpTimeout === 0 ? handleResendOtp : undefined}
                disabled={otpTimeout > 0 || loading}
              >
                {otpTimeout > 0 ? "Resend later" : "Resend OTP"}
              </button>
              <button
                type="button"
                className="btn btn-link p-0 small"
                onClick={() => setMode("forgot_phone")}
              >
                Change Phone
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
